// ===================================
// ENHANCED SEARCH PAGE WITH ADVANCED RAG - src/pages/Search.tsx
// ===================================
import React, { useState, useEffect } from "react";
import { useAuth } from "../contexts/AuthContext";
import { useNotifications } from "../contexts/NotificationContext";
import { searchApi, documentApi } from "../services/api";
import Header from "../components/Header";
import LoadingSpinner from "../components/LoadingSpinner";
import {
  Search as SearchIcon,
  Filter,
  X,
  Download,
  ExternalLink,
  BookOpen,
  ChevronDown,
  ChevronUp,
  Sliders,
  Clock,
  Zap,
  BarChart3,
  FileText,
  Brain,
  Target,
  Copy,
  Share2,
  Save,
  History,
} from "lucide-react";
import {
  SearchResult,
  SearchStats,
  Document,
  RAGResponse,
  SearchOptions,
} from "../types";
import { notifyError, notifySuccess } from "../utils/Toastify";

const Search: React.FC = () => {
  const { user } = useAuth();

  // Search state
  const [query, setQuery] = useState("");
  const [results, setResults] = useState<SearchResult[]>([]);
  const [ragResponse, setRagResponse] = useState<RAGResponse | null>(null);
  const [isLoading, setIsLoading] = useState(false);
  const [searchMode, setSearchMode] = useState<"rag" | "similarity">("rag");
  const [searchStats, setSearchStats] = useState<SearchStats | null>(null);
  const [documents, setDocuments] = useState<Document[]>([]);

  // Advanced filters
  const [showAdvancedFilters, setShowAdvancedFilters] = useState(false);
  const [filters, setFilters] = useState<SearchOptions>({
    limit: 10,
    similarity_threshold: 0.1,
    search_type: "hybrid",
    file_types: [],
    document_ids: [],
  });

  // RAG settings
  const [ragSettings, setRagSettings] = useState({
    temperature: 0.1,
    max_tokens: 2000,
    include_citations: true,
    confidence_threshold: 0.7,
  });

  // UI state
  const [searchHistory, setSearchHistory] = useState<string[]>([]);
  const [savedSearches, setSavedSearches] = useState<
    Array<{ id: string; name: string; query: string; filters: SearchOptions }>
  >([]);
  const [showSources, setShowSources] = useState<{
    [resultId: string]: boolean;
  }>({});
  const [selectedResults, setSelectedResults] = useState<Set<string>>(
    new Set()
  );

  // Analytics
  const [searchMetrics, setSearchMetrics] = useState({
    totalSearches: 0,
    avgResponseTime: 0,
    successRate: 0,
  });

  useEffect(() => {
    loadSearchStats();
    loadDocuments();
    loadSearchHistory();
    loadSavedSearches();
  }, []);

  const loadSearchStats = async () => {
    try {
      const response = await searchApi.stats();
      setSearchStats(response.data.search_analytics || response.data.data);
    } catch (error) {}
  };

  const loadDocuments = async () => {
    try {
      const response = await documentApi.list({
        limit: 100,
        processing_status: "completed",
      });
      setDocuments(response.data.documents || response.data);
    } catch (error) {}
  };

  const loadSearchHistory = () => {
    const history = localStorage.getItem("searchHistory");
    if (history) {
      setSearchHistory(JSON.parse(history).slice(0, 10));
    }
  };

  const loadSavedSearches = () => {
    const saved = localStorage.getItem("savedSearches");
    if (saved) {
      setSavedSearches(JSON.parse(saved));
    }
  };

  const saveToSearchHistory = (searchQuery: string) => {
    const history = [
      searchQuery,
      ...searchHistory.filter((q) => q !== searchQuery),
    ].slice(0, 10);
    setSearchHistory(history);
    localStorage.setItem("searchHistory", JSON.stringify(history));
  };

  const saveSearch = () => {
    if (!query.trim()) return;

    const searchId = Date.now().toString();
    const newSavedSearch = {
      id: searchId,
      name: query.slice(0, 50) + (query.length > 50 ? "..." : ""),
      query: query.trim(),
      filters: { ...filters },
    };

    const updated = [newSavedSearch, ...savedSearches].slice(0, 20);
    setSavedSearches(updated);
    localStorage.setItem("savedSearches", JSON.stringify(updated));
    notifySuccess("Search has been saved for future use");
  };

  const loadSavedSearch = (savedSearch: (typeof savedSearches)[0]) => {
    setQuery(savedSearch.query);
    setFilters(savedSearch.filters);
    notifyError(`Loaded "${savedSearch.name}"`);
  };

  const handleSearch = async (e: React.FormEvent) => {
    e.preventDefault();
    if (!query.trim() || isLoading) return;

    const startTime = Date.now();
    setIsLoading(true);
    setResults([]);
    setRagResponse(null);

    try {
      saveToSearchHistory(query.trim());

      if (searchMode === "rag") {
        const response = await searchApi.rag({
          query: query.trim(),
          search_options: filters,
          temperature: ragSettings.temperature,
          max_tokens: ragSettings.max_tokens,
          include_citations: ragSettings.include_citations,
          confidence_threshold: ragSettings.confidence_threshold,
        });

        setRagResponse(response.data);
        setResults(response.data.context_used || []);

        // Update metrics
        const responseTime = Date.now() - startTime;
        setSearchMetrics((prev) => ({
          totalSearches: prev.totalSearches + 1,
          avgResponseTime: Math.round(
            (prev.avgResponseTime * prev.totalSearches + responseTime) /
              (prev.totalSearches + 1)
          ),
          successRate:
            Math.round(
              ((prev.successRate * prev.totalSearches + 100) /
                (prev.totalSearches + 1)) *
                100
            ) / 100,
        }));

        notifySuccess(
          `Generated answer using ${
            response.data.context_used?.length || 0
          } sources`
        );
      } else {
        const response = await searchApi.similar({
          query: query.trim(),
          ...filters,
        });

        setResults(response.data.results || []);

        const responseTime = Date.now() - startTime;
        setSearchMetrics((prev) => ({
          totalSearches: prev.totalSearches + 1,
          avgResponseTime: Math.round(
            (prev.avgResponseTime * prev.totalSearches + responseTime) /
              (prev.totalSearches + 1)
          ),
          successRate:
            Math.round(
              ((prev.successRate * prev.totalSearches + 100) /
                (prev.totalSearches + 1)) *
                100
            ) / 100,
        }));
        notifySuccess(
          `Found ${response.data.results?.length || 0} similar documents`
        );
      }
    } catch (error: any) {
      setSearchMetrics((prev) => ({
        totalSearches: prev.totalSearches + 1,
        avgResponseTime: prev.avgResponseTime,
        successRate:
          Math.round(
            ((prev.successRate * prev.totalSearches) /
              (prev.totalSearches + 1)) *
              100
          ) / 100,
      }));

      notifyError(
        error.response?.data?.error?.message ||
          "An error occurred during search"
      );
    } finally {
      setIsLoading(false);
    }
  };

  const handleFilterChange = (filterType: keyof SearchOptions, value: any) => {
    setFilters((prev) => ({
      ...prev,
      [filterType]: value,
    }));
  };

  const clearFilters = () => {
    setFilters({
      limit: 10,
      similarity_threshold: 0.1,
      search_type: "hybrid",
      file_types: [],
      document_ids: [],
    });
  };

  const exportResults = () => {
    const exportData = {
      query,
      search_mode: searchMode,
      filters,
      rag_response: ragResponse,
      results: results.map((result) => ({
        ...result,
        selected: selectedResults.has(result.chunk_id),
      })),
      exported_at: new Date().toISOString(),
      user: user?.name,
      search_stats: searchStats,
    };

    const dataStr = JSON.stringify(exportData, null, 2);
    const dataBlob = new Blob([dataStr], { type: "application/json" });
    const url = URL.createObjectURL(dataBlob);

    const link = document.createElement("a");
    link.href = url;
    link.download = `search-results-${
      new Date().toISOString().split("T")[0]
    }.json`;
    link.click();

    URL.revokeObjectURL(url);
    notifySuccess("Search results exported successfully");
  };

  const copyToClipboard = (text: string, type: string) => {
    navigator.clipboard.writeText(text).then(() => {
      notifySuccess(`${type} copied to clipboard`);
    });
  };

  const getSimilarityColor = (similarity: number) => {
    if (similarity >= 0.8) return "text-green-600 bg-green-100";
    if (similarity >= 0.6) return "text-[#8b3dff] bg-purple-100";
    if (similarity >= 0.4) return "text-yellow-600 bg-yellow-100";
    return "text-red-600 bg-red-100";
  };

  const highlightText = (text: string, query: string) => {
    if (!query.trim()) return text;

    const queryWords = query.trim().split(/\s+/);
    let highlightedText = text;

    queryWords.forEach((word) => {
      const regex = new RegExp(`(${word})`, "gi");
      highlightedText = highlightedText.replace(
        regex,
        '<mark class="bg-yellow-200 px-1 rounded">$1</mark>'
      );
    });

    return highlightedText;
  };

  const toggleResultSelection = (resultId: string) => {
    setSelectedResults((prev) => {
      const newSet = new Set(prev);
      if (newSet.has(resultId)) {
        newSet.delete(resultId);
      } else {
        newSet.add(resultId);
      }
      return newSet;
    });
  };

  const availableFileTypes = Array.from(
    new Set(documents.map((doc) => doc.file_type))
  );

  return (
    <div className="min-h-screen bg-[#0f172a]">
      <Header
        title="Advanced Document Search"
        subtitle={
          searchStats
            ? `Search across ${searchStats.total_documents} documents with ${searchStats.chunks_with_embeddings} indexed chunks`
            : "Loading search capabilities..."
        }
      />

      <main className="max-w-7xl mx-auto py-6 sm:px-6 lg:px-8">
        <div className="px-4 py-6 sm:px-0">
          {/* Search Analytics Banner */}
          {searchStats && (
            <div className="mb-6 grid grid-cols-2 gap-4 lg:grid-cols-5">
              <div className="card p-4 !bg-[#1e293b] !border-[#334155] border">
                <div className="flex items-center">
                  <FileText className="w-8 h-8 text-[#8b3dff]" />
                  <div className="ml-3">
                    <div className="text-2xl font-bold text-[#8b3dff]">
                      {searchStats.total_documents || 0}
                    </div>
                    <div className="text-sm text-[#64748b]">Documents</div>
                  </div>
                </div>
              </div>
              <div className="card p-4 !bg-[#1e293b] !border-[#334155] border">
                <div className="flex items-center">
                  <Brain className="w-8 h-8 text-green-600" />
                  <div className="ml-3">
                    <div className="text-2xl font-bold text-green-600">
                      {searchStats.chunks_with_embeddings || 0}
                    </div>
                    <div className="text-sm text-[#64748b]">
                      Searchable Chunks
                    </div>
                  </div>
                </div>
              </div>
              <div className="card p-4 !bg-[#1e293b] !border-[#334155] border">
                <div className="flex items-center">
                  <Target className="w-8 h-8 text-purple-600" />
                  <div className="ml-3">
                    <div className="text-2xl font-bold text-purple-600">
                      {searchMetrics.totalSearches}
                    </div>
                    <div className="text-sm text-[#64748b]">Searches Run</div>
                  </div>
                </div>
              </div>
              <div className="card p-4 !bg-[#1e293b] !border-[#334155] border">
                <div className="flex items-center">
                  <Zap className="w-8 h-8 text-orange-600" />
                  <div className="ml-3">
                    <div className="text-2xl font-bold text-orange-600">
                      {searchMetrics.avgResponseTime}ms
                    </div>
                    <div className="text-sm text-[#64748b]">Avg Response</div>
                  </div>
                </div>
              </div>
              <div className="card p-4 !bg-[#1e293b] !border-[#334155] border">
                <div className="flex items-center">
                  <BarChart3 className="w-8 h-8 text-indigo-600" />
                  <div className="ml-3">
                    <div className="text-2xl font-bold text-indigo-600">
                      {searchMetrics.successRate}%
                    </div>
                    <div className="text-sm text-[#64748b]">Success Rate</div>
                  </div>
                </div>
              </div>
            </div>
          )}

          {/* Search Interface */}
          <div className="card mb-6 !bg-[#1e293b] !border-[#334155] border">
            <div className="p-6 border-b !border-[#334155]">
              <div className="flex items-center justify-between mb-4">
                <h2 className="text-lg font-medium text-white">
                  Search Your Knowledge Base
                </h2>
                <div className="flex items-center space-x-4">
                  {/* Search Mode Toggle */}
                  <div className="flex items-center space-x-2">
                    <label className="text-sm text-white">Mode:</label>
                    <select
                      value={searchMode}
                      onChange={(e) =>
                        setSearchMode(e.target.value as "rag" | "similarity")
                      }
                      className="border !border-[#334155] rounded px-2 py-1 text-sm focus:outline-none focus:ring-2 focus:ring-[#8b3dff]"
                    >
                      <option value="rag">🤖 RAG (Answer + Sources)</option>
                      <option value="similarity">
                        🔍 Similarity Search Only
                      </option>
                    </select>
                  </div>

                  <button
                    onClick={() => setShowAdvancedFilters(!showAdvancedFilters)}
                    className="flex items-center space-x-1 text-sm text-[#8b3dff] hover:text-purple-800"
                  >
                    <Sliders className="w-4 h-4" />
                    <span>Advanced</span>
                  </button>
                </div>
              </div>

              {/* Search Form */}
              <form onSubmit={handleSearch} className="space-y-4">
                <div className="flex space-x-4">
                  <input
                    type="text"
                    value={query}
                    onChange={(e) => setQuery(e.target.value)}
                    placeholder="Search your documents... (e.g., 'What are the key findings?', 'Find information about training procedures')"
                    className="flex-1 text-white px-4 py-2 rounded-md focus:outline-[#8b3dff]  outline-none border-[#334155] border"
                    disabled={isLoading}
                    style={{ backgroundColor: "#020617" }}
                  />
                  <button
                    type="submit"
                    disabled={
                      !query.trim() ||
                      isLoading ||
                      (searchStats?.chunks_with_embeddings || 0) === 0
                    }
                    className="bg-[#8b3dff] text-white rounded-md px-8 py-3 disabled:opacity-50 disabled:cursor-not-allowed font-medium flex items-center space-x-2"
                  >
                    {isLoading ? (
                      <LoadingSpinner size="sm" message="" />
                    ) : (
                      <>
                        <SearchIcon className="w-5 h-5" />
                        <span>
                          {searchMode === "rag" ? "🤖 Ask" : "🔍 Search"}
                        </span>
                      </>
                    )}
                  </button>
                </div>

                {/* Quick Actions */}
                <div className="flex items-center justify-between">
                  <div className="flex items-center space-x-4">
                    {/* Search History */}
                    {searchHistory.length > 0 && (
                      <div className="flex items-center space-x-2">
                        <History className="w-4 h-4 text-[#64748b]" />
                        <span className="text-sm text-[#64748b]">Recent:</span>
                        {searchHistory
                          .slice(0, 3)
                          .map((historyQuery, index) => (
                            <button
                              key={index}
                              onClick={() => setQuery(historyQuery)}
                              className="text-xs bg-[#1e293b] text-white px-2 py-1 rounded hover:bg-gray-200 transition-colors"
                            >
                              {historyQuery.length > 30
                                ? historyQuery.substring(0, 30) + "..."
                                : historyQuery}
                            </button>
                          ))}
                      </div>
                    )}
                  </div>

                  <div className="flex items-center space-x-2">
                    <button
                      type="button"
                      onClick={saveSearch}
                      disabled={!query.trim()}
                      className="flex items-center space-x-1 text-sm text-green-600 hover:text-green-800 disabled:opacity-50 cursor-pointer"
                    >
                      <Save className="w-4 h-4" />
                      <span>Save</span>
                    </button>

                    {savedSearches.length > 0 && (
                      <div className="relative">
                        <select
                          onChange={(e) => {
                            const savedSearch = savedSearches.find(
                              (s) => s.id === e.target.value
                            );
                            if (savedSearch) loadSavedSearch(savedSearch);
                          }}
                          value=""
                          className="text-sm border border-[#334155]  rounded px-2 py-1 focus:outline-none focus:ring-2 focus:ring-[#8b3dff]"
                        >
                          <option value="">Load Saved Search</option>
                          {savedSearches.map((saved) => (
                            <option key={saved.id} value={saved.id}>
                              {saved.name}
                            </option>
                          ))}
                        </select>
                      </div>
                    )}
                  </div>
                </div>
              </form>
            </div>

            {/* Advanced Filters */}
            {showAdvancedFilters && (
              <div className="p-6 border-b !border-[#334155] bg-[#1e293b]">
                <div className="grid grid-cols-1 md:grid-cols-3 gap-6">
                  {/* Basic Filters */}
                  <div className="space-y-4">
                    <h4 className="text-sm font-medium text-white">
                      Search Parameters
                    </h4>
                    <div>
                      <label className="block text-sm font-medium text-white mb-1">
                        Max Results
                      </label>
                      <input
                        type="number"
                        min="1"
                        max="50"
                        value={filters.limit}
                        onChange={(e) =>
                          handleFilterChange("limit", parseInt(e.target.value))
                        }
                        className="w-full text-white px-4 py-2 rounded-md focus:outline-[#8b3dff] focus:ring-0 outline-none"
                        style={{ backgroundColor: "#020617" }}
                      />
                    </div>

                    <div>
                      <label className="block text-sm font-medium text-white mb-1">
                        Similarity Threshold
                      </label>
                      <input
                        type="range"
                        min="0"
                        max="1"
                        step="0.1"
                        value={filters.similarity_threshold}
                        onChange={(e) =>
                          handleFilterChange(
                            "similarity_threshold",
                            parseFloat(e.target.value)
                          )
                        }
                        className="w-full h-2 rounded-lg !bg-[#020617] accent-[#8b3dff] cursor-pointer"
                      />
                      <div className="text-xs text-[#64748b] mt-1">
                        {(filters.similarity_threshold! * 100).toFixed(0)}%
                        minimum similarity
                      </div>
                    </div>

                    <div>
                      <label className="block text-sm font-medium text-white mb-1">
                        Search Type
                      </label>
                      <select
                        value={filters.search_type}
                        onChange={(e) =>
                          handleFilterChange("search_type", e.target.value)
                        }
                        className="w-full border border-[#334155] rounded px-3 py-2 text-sm focus:outline-none focus:ring-2 focus:ring-[#8b3dff]"
                      >
                        <option value="hybrid">🔄 Hybrid (Recommended)</option>
                        <option value="semantic">🧠 Semantic Only</option>
                        <option value="keyword">🔤 Keyword Only</option>
                      </select>
                    </div>
                  </div>

                  {/* RAG Settings */}
                  {searchMode === "rag" && (
                    <div className="space-y-4">
                      <h4 className="text-sm font-medium text-white">
                        RAG Settings
                      </h4>
                      <div>
                        <label className="block text-sm font-medium text-white mb-1">
                          Temperature
                        </label>
                        <input
                          type="range"
                          min="0"
                          max="1"
                          step="0.1"
                          value={ragSettings.temperature}
                          onChange={(e) =>
                            setRagSettings((prev) => ({
                              ...prev,
                              temperature: parseFloat(e.target.value),
                            }))
                          }
                          className="w-full h-2 rounded-lg !bg-[#020617] accent-[#8b3dff] cursor-pointer"
                        />
                        <div className="text-xs text-[#64748b] mt-1">
                          {ragSettings.temperature} (0 = focused, 1 = creative)
                        </div>
                      </div>

                      <div>
                        <label className="block text-sm font-medium text-white mb-1">
                          Max Tokens
                        </label>
                        <input
                          type="number"
                          min="100"
                          max="4000"
                          step="100"
                          value={ragSettings.max_tokens}
                          onChange={(e) =>
                            setRagSettings((prev) => ({
                              ...prev,
                              max_tokens: parseInt(e.target.value),
                            }))
                          }
                          className="w-full text-white px-4 py-2 rounded-md focus:outline-[#8b3dff] focus:ring-0 outline-none"
                          style={{ backgroundColor: "#020617" }}
                        />
                      </div>

                      <div className="space-y-2">
                        <label className="flex items-center">
                          <input
                            type="checkbox"
                            checked={ragSettings.include_citations}
                            onChange={(e) =>
                              setRagSettings((prev) => ({
                                ...prev,
                                include_citations: e.target.checked,
                              }))
                            }
                            className="rounded w-4 h-4 border-[#334155] accent-[#8b3dff] focus:ring-0 focus:outline-none"
                          />
                          <span className="ml-2 text-sm text-white">
                            Include Citations
                          </span>
                        </label>
                      </div>
                    </div>
                  )}

                  {/* Document Filters */}
                  <div className="space-y-4">
                    <h4 className="text-sm font-medium text-white">
                      Document Filters
                    </h4>
                    <div>
                      <label className="block text-sm font-medium text-white mb-1">
                        File Types
                      </label>
                      <select
                        multiple
                        value={filters.file_types}
                        onChange={(e) =>
                          handleFilterChange(
                            "file_types",
                            Array.from(
                              e.target.selectedOptions,
                              (option) => option.value
                            )
                          )
                        }
                        className="w-full border border-[#334155] rounded px-3 py-2 text-sm h-20 focus:outline-none focus:ring-2 focus:ring-[#8b3dff] text-white"
                        style={{ backgroundColor: "#020617" }}
                      >
                        {availableFileTypes.map((type) => (
                          <option key={type} value={type}>
                            {type.toUpperCase()}
                          </option>
                        ))}
                      </select>
                    </div>

                    <div>
                      <label className="block text-sm font-medium text-white mb-1">
                        Specific Documents
                      </label>
                      <select
                        multiple
                        value={filters.document_ids}
                        onChange={(e) =>
                          handleFilterChange(
                            "document_ids",
                            Array.from(
                              e.target.selectedOptions,
                              (option) => option.value
                            )
                          )
                        }
                        className="w-full border border-[#334155] rounded px-3 py-2 text-sm h-20 focus:outline-none focus:ring-2 focus:ring-[#8b3dff] text-white"
                        style={{ backgroundColor: "#020617" }}
                      >
                        {documents.slice(0, 20).map((doc) => (
                          <option key={doc.id} value={doc.id}>
                            {doc.original_filename}
                          </option>
                        ))}
                      </select>
                    </div>
                  </div>
                </div>

                <div className="mt-6 flex justify-end space-x-3">
                  <button
                    onClick={clearFilters}
                    className="text-sm text-white hover:text-white flex items-center space-x-1"
                  >
                    <X className="w-4 h-4" />
                    <span>Clear Filters</span>
                  </button>
                  <button
                    onClick={() => setShowAdvancedFilters(false)}
                    className="btn-secondary text-sm"
                  >
                    Apply Filters
                  </button>
                </div>
              </div>
            )}
          </div>

          {/* Loading State */}
          {isLoading && (
            <div className="flex justify-center py-8">
              <LoadingSpinner
                size="lg"
                message={
                  searchMode === "rag"
                    ? "Searching documents and generating intelligent answer..."
                    : "Searching documents..."
                }
              />
            </div>
          )}

          {/* RAG Response */}
          {ragResponse && searchMode === "rag" && (
            <div className="card mb-6">
              <div className="p-6 border-b !border-[#334155]">
                <div className="flex items-center justify-between">
                  <h3 className="text-lg font-medium text-white flex items-center">
                    🤖 AI Generated Answer
                    {ragResponse.confidence_score !== undefined && (
                      <span
                        className={`ml-3 px-2 py-1 text-xs font-medium rounded ${
                          ragResponse.confidence_score >= 0.8
                            ? "bg-green-100 text-green-800"
                            : ragResponse.confidence_score >= 0.6
                            ? "bg-purple-100 text-purple-800"
                            : "bg-yellow-100 text-yellow-800"
                        }`}
                      >
                        {(ragResponse.confidence_score * 100).toFixed(1)}%
                        confidence
                      </span>
                    )}
                  </h3>
                  <div className="flex items-center space-x-2">
                    <button
                      onClick={() =>
                        copyToClipboard(ragResponse.response, "Answer")
                      }
                      className="flex items-center space-x-1 text-sm text-[#8b3dff] hover:text-purple-800"
                    >
                      <Copy className="w-4 h-4" />
                      <span>Copy</span>
                    </button>
                    <button
                      onClick={() =>
                        copyToClipboard(
                          JSON.stringify(
                            {
                              query,
                              answer: ragResponse.response,
                              confidence: ragResponse.confidence_score,
                              sources: ragResponse.source_documents,
                            },
                            null,
                            2
                          ),
                          "Full Response"
                        )
                      }
                      className="flex items-center space-x-1 text-sm text-green-600 hover:text-green-800"
                    >
                      <Share2 className="w-4 h-4" />
                      <span>Share</span>
                    </button>
                  </div>
                </div>
              </div>
              <div className="p-6">
                <div className="prose max-w-none">
                  <div className="whitespace-pre-wrap text-white leading-relaxed">
                    {ragResponse.response}
                  </div>
                </div>

                {/* Response Metadata */}
                {ragResponse.response_metadata && (
                  <div className="mt-4 p-3 bg-[#1e293b] rounded-lg">
                    <div className="text-xs text-white grid grid-cols-2 md:grid-cols-4 gap-4">
                      <div>
                        <span className="font-medium">Model:</span>{" "}
                        {ragResponse.response_metadata.model}
                      </div>
                      <div>
                        <span className="font-medium">Generation Time:</span>{" "}
                        {ragResponse.response_metadata.generation_time_ms}ms
                      </div>
                      <div>
                        <span className="font-medium">Chunks Used:</span>{" "}
                        {ragResponse.response_metadata.context_chunks_used}
                      </div>
                      <div>
                        <span className="font-medium">Total Time:</span>{" "}
                        {ragResponse.response_metadata.total_time_ms}ms
                      </div>
                    </div>
                  </div>
                )}
              </div>
            </div>
          )}

          {/* Search Results */}
          {results.length > 0 && (
            <div className="card !bg-[#1e293b] !border-[#334155] border">
              <div className="p-6 border-b !border-[#334155]">
                <div className="flex items-center justify-between">
                  <h3 className="text-lg font-medium text-white">
                    {searchMode === "rag" ? "Sources Used" : "Search Results"} (
                    {results.length})
                    {selectedResults.size > 0 && (
                      <span className="ml-2 text-sm text-[#8b3dff]">
                        ({selectedResults.size} selected)
                      </span>
                    )}
                  </h3>
                  <div className="flex items-center space-x-2">
                    {selectedResults.size > 0 && (
                      <button
                        onClick={() => setSelectedResults(new Set())}
                        className="text-sm text-white hover:text-white"
                      >
                        Clear selection
                      </button>
                    )}
                    <button
                      onClick={exportResults}
                      className="flex items-center space-x-1 text-sm text-[#8b3dff] hover:text-purple-800"
                    >
                      <Download className="w-4 h-4" />
                      <span>Export</span>
                    </button>
                  </div>
                </div>
              </div>
              <div className="divide-y divide-[#334155]">
                {results.map((result, index) => (
                  <div
                    key={result.chunk_id}
                    className="p-6 hover:bg-[#1e293b] transition-colors"
                  >
                    <div className="flex items-start justify-between">
                      <div className="flex items-start space-x-3 flex-1 min-w-0">
                        <input
                          type="checkbox"
                          checked={selectedResults.has(result.chunk_id)}
                          onChange={() =>
                            toggleResultSelection(result.chunk_id)
                          }
                          className="mt-1 rounded border-[#334155] text-[#8b3dff] focus:ring-[#8b3dff]"
                        />

                        <div className="flex-1 min-w-0">
                          <div className="flex items-center space-x-3 mb-2">
                            <span className="text-sm font-medium text-white">
                              #{index + 1}
                            </span>
                            <span className="text-sm font-medium text-[#8b3dff] flex items-center space-x-1">
                              <span>📄</span>
                              <span>{result.document.filename}</span>
                            </span>
                            <span className="text-xs text-[#64748b]">
                              {result.document.file_type.toUpperCase()} • Chunk{" "}
                              {result.chunk_index}
                            </span>

                            {/* Similarity/Relevance Scores */}
                            <div className="flex items-center space-x-2">
                              {result.similarity_score !== undefined && (
                                <span
                                  className={`px-2 py-1 text-xs font-medium rounded ${getSimilarityColor(
                                    result.similarity_score
                                  )}`}
                                >
                                  {(result.similarity_score * 100).toFixed(1)}%
                                  semantic
                                </span>
                              )}
                              {result.relevance_score !== undefined && (
                                <span className="px-2 py-1 text-xs font-medium rounded bg-[#1e293b] text-white">
                                  {result.relevance_score.toFixed(2)} keyword
                                </span>
                              )}
                              {result.combined_score !== undefined && (
                                <span className="px-2 py-1 text-xs font-medium rounded bg-purple-100 text-purple-800">
                                  {result.combined_score.toFixed(2)} combined
                                </span>
                              )}
                            </div>
                          </div>

                          <div
                            className="text-sm text-white leading-relaxed mb-2"
                            dangerouslySetInnerHTML={{
                              __html: highlightText(result.chunk_text, query),
                            }}
                          />

                          <div className="flex items-center space-x-4 text-xs text-[#64748b]">
                            <span>{result.word_count} words</span>
                            <span>•</span>
                            <span>{result.char_count} characters</span>
                            <span>•</span>
                            <span>{result.document.business_name}</span>
                            {result.document.uploaded_by && (
                              <>
                                <span>•</span>
                                <span>by {result.document.uploaded_by}</span>
                              </>
                            )}
                          </div>
                        </div>
                      </div>

                      <div className="ml-4 flex items-center space-x-2">
                        <button
                          onClick={() =>
                            copyToClipboard(result.chunk_text, "Text chunk")
                          }
                          className="p-2 text-[#64748b] hover:text-white rounded-lg hover:bg-[#1e293b]"
                          title="Copy text"
                        >
                          <Copy className="w-4 h-4" />
                        </button>
                        <button
                          className="p-2 text-[#64748b] hover:text-white rounded-lg hover:bg-[#1e293b]"
                          title="View document"
                        >
                          <ExternalLink className="w-4 h-4" />
                        </button>
                      </div>
                    </div>
                  </div>
                ))}
              </div>
            </div>
          )}

          {/* No Results */}
          {!isLoading && query && results.length === 0 && (
            <div className="card !bg-[#1e293b] !border-[#334155] border mb-6">
              <div className="p-8 text-center">
                <div className="text-4xl text-[#64748b] mb-4">🔍</div>
                <p className="text-lg text-white mb-2">No results found</p>
                <p className="text-sm text-[#64748b] mb-4">
                  Try adjusting your search query, lowering the similarity
                  threshold, or changing the search type
                </p>
                <div className="flex justify-center space-x-4">
                  <button
                    onClick={() =>
                      handleFilterChange("similarity_threshold", 0.1)
                    }
                    className="btn-secondary text-sm"
                  >
                    Lower Threshold
                  </button>
                  <button
                    onClick={() => handleFilterChange("search_type", "hybrid")}
                    className="btn-secondary text-sm"
                  >
                    Try Hybrid Search
                  </button>
                </div>
              </div>
            </div>
          )}

          {/* No Documents Warning */}
          {(searchStats?.chunks_with_embeddings || 0) === 0 && (
            <div className="border  bg-[#1e293b] border-[#334155] rounded-lg p-6">
              <div className="text-center">
                <div className="text-4xl text-yellow-400 mb-4">📚</div>
                <p className="text-lg text-white mb-2">
                  No searchable documents yet
                </p>
                <p className="text-sm text-[#64748b] mb-4">
                  Upload and process documents first to enable search
                  functionality
                </p>
                <div className="flex justify-center space-x-4">
                  <button
                    onClick={() => (window.location.href = "/documents")}
                    className="bg-[#8b3dff] px-3 py-1 rounded-md text-white"
                  >
                    Upload Documents
                  </button>
                  <button
                    onClick={() => (window.location.href = "/dashboard")}
                    className="btn-secondary"
                  >
                    Go to Dashboard
                  </button>
                </div>
              </div>
            </div>
          )}
        </div>
      </main>
    </div>
  );
};

export default Search;
